import type { IFilterableListItem, IOmit, ISuggestProps } from '@sixui/ui';
import { forwardRef } from 'react';
import { faCorner } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Breadcrumbs, Field, Skeleton, Suggest, Text } from '@sixui/ui';
import { breadcrumbsTokens } from '@sixui/ui/components/Breadcrumbs/Breadcrumbs.stylex';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { ITree } from '~/helpers/tree';
import type { IApiGetCategoryListItemResponseBody } from '~/lib/api/modules/Categories';
import type { ICategoryFilter } from './categoryFilterPredicate';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { findPath, makeTree } from '~/helpers/tree';
import { useCategoriesQuery } from '~/lib/api/modules/Categories/hooks/useCategoriesQuery';
import { categoryFilterPredicate } from './categoryFilterPredicate';

export type ICategorySelectorProps = IOmit<ISuggestProps, 'items'> & {
  appId: string;
  filter?: ICategoryFilter;
  maxDepth?: number;
};

const styles = stylex.create({
  treeLeafIcon: (depth: number) => ({
    marginLeft: `calc((16px + 0.5rem) * ${depth - 1})`,
    marginRight: '0.5rem',
    width: 16,
    height: 16,
    rotate: '180deg',
  }),
  breadcrumbs: {
    paddingLeft: '1rem',
    paddingRight: 8,
    [breadcrumbsTokens.itemColor]: colorSchemeTokens.onSurfaceVariant,
  },
});

const renderOptionsTree = (
  tree: ITree<IApiGetCategoryListItemResponseBody>,
): Array<IFilterableListItem> =>
  tree
    .map(({ _id, name, depth, children }) => [
      {
        value: _id,
        label: name,
        icon:
          depth > 0 ? (
            <FontAwesomeIcon
              {...stylex.props(styles.treeLeafIcon(depth))}
              icon={faCorner}
            />
          ) : undefined,
      },
      ...(children && children?.length > 0 ? renderOptionsTree(children) : []),
    ])
    .flat();

const renderPath = (
  categoryId: string,
  categories: Array<IApiGetCategoryListItemResponseBody>,
): React.ReactNode => {
  const path = findPath(categories, categoryId).slice(1);
  if (path.length <= 0) {
    return null;
  }

  return (
    <Text as={Breadcrumbs} variant="body" size="lg" showTrailingSeparator>
      {path.map(({ _id, name }) => (
        <span key={_id}>{name}</span>
      ))}
    </Text>
  );
};

export const CategorySelector = forwardRef<
  HTMLInputElement,
  ICategorySelectorProps
>(function CategorySelector(props, forwardedRef) {
  const { getValueFieldProps, appId, filter, maxDepth, ...other } = props;
  const categoriesQuery = useCategoriesQuery({ appId });
  const { t } = useTranslation();

  const tree = categoriesQuery.data?.categories
    ? makeTree(
        filter
          ? categoriesQuery.data.categories.filter((category) =>
              categoryFilterPredicate(category, filter),
            )
          : categoriesQuery.data.categories,
        maxDepth,
      )
    : undefined;

  const loaded = !!tree || !categoriesQuery.isLoading;
  const items = tree
    ? [
        {
          value: '',
          label: t('CategorySelector.rootCategory'),
        },
        ...renderOptionsTree(tree),
      ]
    : [];

  return categoriesQuery.isError ? (
    <ErrorMessage error={categoriesQuery.error} />
  ) : loaded ? (
    <Suggest
      {...other}
      items={items}
      getValueFieldProps={(renderProps, item) => ({
        ...getValueFieldProps?.(renderProps, item),
        start:
          item?.value && categoriesQuery.data?.categories
            ? renderPath(item.value, categoriesQuery.data?.categories)
            : undefined,
      })}
      ref={forwardedRef}
    />
  ) : categoriesQuery.isLoading ? (
    <Skeleton>
      <Field />
    </Skeleton>
  ) : null;
});
